/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { createPortal } from 'react-dom';
import { toast, ToastContainer } from 'react-toastify';

import {
  AnnotationSectionPages as SectionPages,
  AnnotationSectionToolbox as SectionToolbox,
  HistoryModal,
  SectionCanvasAnnotable,
  SectionLayers,
  _object,
} from '@mairistem/administres-utils';
import { Button, Dimmer, Header, Icon, Loader } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { useLocation } from 'react-router-dom';

import { BASE_URL_ETATCIVIL } from '@/constants';
import {
  useAnnotationsTypes,
  useImagesPersonnalisees,
  useLayers,
  useMentions,
} from '@/hooks';
import type { Acte, TiffPage } from '@/types';
import { TiffDecoder, isTypeActe } from '@/utils';

import { rtf2text } from '@mairistem/administres-rtf-parser';
import { SelectionActeModal } from './SelectionActe';
import ConfirmationModal from './ConfirmationModal';
import { CanvasContextProvider } from '@/contexts';

const Gestionnaire = () => {
  const [openSelectionModal, setOpenSelectionModal] = React.useState(false);
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [openHistoryModal, setOpenHistoryModal] = React.useState(false);
  const [acte, setActe] = React.useState<Acte | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [pages, setPages] = React.useState<Array<TiffPage>>([]);
  // The number (not the index!) of the selected page
  const [selectedPage, setSelectedPage] = React.useState(0);
  // The ID of the selected layer
  const [selectedLayer, setSelectedLayer] = React.useState<number | string>(0);
  // If we are in standalone
  const [standalone, setStandalone] = React.useState(true);
  // If the document is a tiff
  const [isTiff, setIsTiff] = React.useState(false);
  /** If we need to blocked the page in standalone  */
  const [blocked, setBlocked] = React.useState(true);
  /** If ininity mode customer */
  const [infinity, setInfinity] = React.useState(false);

  const { search } = useLocation();
  const query = React.useMemo(() => new URLSearchParams(search), [search]);
  const identifiant = parseInt(query.get('idDocument'), 10);
  const type = query.get('typeActe');

  /*
    Le mode standalone porte sur un document précis, désigné par l'URL : c'est
    la portée `document` du hook partagé. Hors standalone, l'acte est choisi
    dans la modale et l'API résout elle-même son document image.
  */
  const layers = useLayers(
    identifiant || acte?.identifiant || 0,
    acte?.type,
    standalone ? 'document' : 'acte',
  );
  const images = useImagesPersonnalisees(layers);
  /*
    Les listes de la boîte à outils sont chargées ici depuis que le composant
    est partagé : l'application qui le monte reste maîtresse de leur
    rafraîchissement.
  */
  const annotationsTypes = useAnnotationsTypes(acte?.identifiant);
  const mentions = useMentions(acte?.identifiant || 0, acte?.type);

  const handleResponseDownload = React.useCallback((data) => {
    const { document: documentString, type: typeTiff } = data as Record<
      string,
      string
    >;

    if (!documentString) {
      toast("Aucun document reçu de l'API", { type: 'error' });
      return;
    }

    let doc: string;
    try {
      const cleanedDocument = documentString.replace(/\s/g, '');
      doc = atob(cleanedDocument);
    } catch (_error) {
      toast('Erreur lors du décodage base64 du document', { type: 'error' });
      return;
    }

    // si ca commence comme du rtf:
    if (doc.startsWith('{\\rtf') || typeTiff === 'rtf' || typeTiff === 'RTF') {
      const rtfContent = rtf2text(doc);
      setPages([{ src: rtfContent, pageNumber: 1 }]);
    } else {
      try {
        const decoder = TiffDecoder.fromBinaryString(doc);
        const decodedPages = decoder.getPages();
        setPages(decodedPages);
      } catch (_error) {
        toast('Erreur lors du décodage du document TIFF', { type: 'error' });
      }
    }

    if (_.toLower(typeTiff) === 'tiff' || _.toLower(typeTiff) === 'tif') {
      setIsTiff(true);
    } else {
      setIsTiff(false);
    }
  }, []);
  const handleSelectedActe = React.useCallback(
    (act: Acte) => {
      setLoading(true);
      setActe(act);
      setSelectedLayer(0);
      setSelectedPage(1);
      setOpenSelectionModal(false);

      request
        .get(
          `${BASE_URL_ETATCIVIL}/actes/${act.type}/${act.identifiant}/download`,
          {
            headers: {
              Accept: 'application/json',
            },
          },
        )
        .then((data) => {
          handleResponseDownload(data);
        })
        .catch(() => {
          toast('Erreur lors du chargement du document', { type: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [handleResponseDownload],
  );

  const handleSelectedLayer = React.useCallback(
    (idLayer: number | string) => {
      const layer = _.find(layers.data, { identifiant: idLayer });
      if (!_.isNil(layer)) {
        setSelectedLayer(idLayer);

        if (layer.page !== selectedPage) {
          setSelectedPage(layer.page);
        }
      } else {
        setSelectedLayer(0);
      }
    },
    [layers.data, selectedPage],
  );

  // Use to continue without save the modifications with the second choice of the modal
  const handleContinueWithoutSaveModal = React.useCallback(() => {
    setOpenConfirmModal(false);
    setOpenSelectionModal(true);
  }, []);

  // Use to save the layers with the third choice of the confirm modal
  const handleSaveLayersModal = React.useCallback(() => {
    layers.save();
    setOpenConfirmModal(false);
    setOpenSelectionModal(true);
  }, [layers.save]);

  // Use to open the selection modal or confirm modal
  const handleOpenModal = React.useCallback(() => {
    if (layers.updated) {
      setOpenConfirmModal(true);
    } else {
      setOpenSelectionModal(true);
    }
  }, [layers.updated]);

  const handleOpenHistoryModal = React.useCallback(() => {
    setOpenHistoryModal(true);
  }, []);

  const handleAddLayer = React.useCallback(
    (data) => {
      setSelectedLayer(layers.add(data)?.identifiant);
    },
    [layers.add],
  );

  let displayName = '';

  switch (acte?.type) {
    case 'reconnaissance':
    case 'naissance':
      displayName = acte.nomClassementEnfant;
      break;

    case 'deces':
      displayName = acte.nomClassementDefunt;
      break;

    case 'mariage':
      displayName = `${acte.conjoint1} & ${acte.conjoint2}`;
      break;

    default:
      break;
  }
  React.useEffect(() => {
    if (!_.isFinite(identifiant) || identifiant <= 0 || !isTypeActe(type)) {
      setBlocked(false);
      setStandalone(false);
      return;
    }

    setLoading(true);
    setSelectedLayer(0);
    setSelectedPage(1);

    request
      .get(`${BASE_URL_ETATCIVIL}/documents/${identifiant}/merge`)
      .then((data) => {
        // @ts-expect-error types are wrong
        setActe({ ...data?.acte, type });
        handleResponseDownload(data);
        setBlocked(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [identifiant, type, handleResponseDownload]);

  const handleDownload = React.useCallback(() => {
    layers.save().then(() => {
      setLoading(true);

      // on utilise pas request.download parce que ça
      // ne fonctionne pas avec la gateway en standalone
      // le header Content-Disposition est pas interprété
      // (alors qu'il est bien dans les headers de la réponse)
      request
        .get(
          standalone
            ? `${BASE_URL_ETATCIVIL}/documents/${identifiant}/download?${_object.toQueryString({ pages: _.size(pages), api: 0 })}`
            : `${BASE_URL_ETATCIVIL}/actes/${acte?.type}/${acte?.identifiant}/merge?${_object.toQueryString({ pages: _.size(pages), api: 0 })}`,
          {
            headers: { Accept: 'image' },
            responseType: 'blob',
          },
        )
        .then((d: Blob) => {
          const url = URL.createObjectURL(d);
          const link = document.createElement('a');
          link.href = url;
          link.download = `acte-${acte?.type}-${acte?.anneeActe}-${acte?.numeroActe}`;
          link.click();

          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            link.remove();
          }, 5000);

          // // Méthode alternative utilisant FileReader pour forcer le téléchargement
          // const reader = new FileReader();
          // reader.onload = function (e) {
          //   const link = document.createElement('a');
          //   link.href = e.target?.result as string;
          //   link.download = `acte-${acte?.type}-${acte?.anneeActe}-${acte?.numeroActe}`;
          //   document.body.appendChild(link);
          //   link.click();
          //   link.remove();
          // };
          // reader.readAsDataURL(d);
        })
        .catch(() => {
          toast("Une erreur est survenue lors du téléchargement de l'acte", {
            type: 'error',
          });
        })
        .finally(() => {
          setLoading(false);
        });
    });
  }, [pages, acte, layers.save, standalone, identifiant]);

  React.useEffect(() => {
    if (!_.isNil(acte)) {
      request
        .get(`${BASE_URL_ETATCIVIL}/infinity`)
        .then((data: Record<string, boolean>) => {
          setInfinity(data?.infinity as boolean);
        });
    }
  }, [acte]);

  return (
    <CanvasContextProvider>
      <div
        style={{
          overflowY: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {createPortal(<ToastContainer />, document.body)}
        <div style={{ backgroundColor: 'white', display: 'flex' }}>
          <Icon
            link
            className="btnSauvegardeActe"
            name={loading || layers.loading ? 'spinner' : 'check'}
            loading={loading || layers.loading}
            size="large"
            title="Sauvegarder"
            onClick={layers.save}
            style={{
              margin: '.6em',
              marginLeft: '1em',
              visibility: layers.updated ? 'visible' : 'hidden',
            }}
          />
          <Icon
            link
            name={loading || layers.loading ? 'spinner' : 'undo'}
            loading={loading || layers.loading}
            size="large"
            title="Annuler les modifications"
            // @ts-expect-error types are wrong
            onClick={layers.refresh}
            style={{
              margin: '.6em',
              marginLeft: '1em',
              visibility: layers.updated ? 'visible' : 'hidden',
            }}
          />
          {!standalone && (
            <Icon
              link
              disabled={!acte}
              loading={loading || layers.loading}
              name={loading || layers.loading ? 'spinner' : 'download'}
              size="large"
              title="Sauvegarder & Télécharger"
              style={{ margin: '.6em', marginLeft: '1em' }}
              onClick={handleDownload}
            />
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              fontWeight: 'bold',
            }}
          >
            {acte && (
              <>
                {!standalone && (
                  <Icon
                    name="search"
                    size="large"
                    style={{ marginRight: '1%' }}
                    link
                    title="Sélection de l'acte"
                    onClick={handleOpenModal}
                  />
                )}
                <div style={{ display: 'flex', height: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      fontWeight: 'bold',
                    }}
                  >
                    {`Acte de ${acte.type} de ${displayName} | N°${acte.numeroActe} de l'année ${acte.anneeActe}`}
                  </div>
                </div>
                <Icon
                  name="history"
                  size="large"
                  title="Historique des modifications"
                  style={{ marginLeft: '1em' }}
                  link
                  onClick={handleOpenHistoryModal}
                />
              </>
            )}
            <ConfirmationModal
              open={openConfirmModal}
              onClose={() => setOpenConfirmModal(false)}
              onContinueWithoutSaveModal={handleContinueWithoutSaveModal}
              onSaveLayersModal={handleSaveLayersModal}
            />
            <SelectionActeModal
              open={openSelectionModal}
              onClose={() => setOpenSelectionModal(false)}
              onSelectedActe={handleSelectedActe}
            />
            <HistoryModal
              open={openHistoryModal}
              onClose={() => setOpenHistoryModal(false)}
              layers={layers}
              pages={pages}
            />
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flex: 1,
            gap: '1em',
            padding: '1em',
            minHeight: 0,
          }}
        >
          <Dimmer active={loading || layers.loading} inverted>
            <Loader indeterminate />
          </Dimmer>
          <div
            style={{
              display: 'flex',
              flex: 4,
              flexDirection: 'column',
              gap: '1em',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                flex: 3,
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <SectionToolbox
                isTiff={isTiff}
                selectedPage={selectedPage}
                onLayerSelected={handleSelectedLayer}
                onAddLayer={handleAddLayer}
                layers={layers.data}
                acteConnu={!_.isNil(acte)}
                annotationsTypes={annotationsTypes}
                mentions={mentions}
                images={images}
                infinity={infinity}
                /*
                  Pas de saisie de mention ici : elle se fait depuis la fiche
                  de l'acte. Sans `onAjouterMention`, la boîte à outils
                  n'affiche pas le bouton.
                */
              />
            </div>
            <div
              style={{
                display: 'flex',
                flex: 2,
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <SectionLayers
                layers={layers.data}
                selectedLayer={selectedLayer}
                onLayerSelected={handleSelectedLayer}
                onDeleteLayer={layers.del}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flex: 9,
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <SectionCanvasAnnotable
              isTiff={isTiff}
              onPageChange={setSelectedPage}
              selectedPage={selectedPage}
              pages={pages}
              layers={layers.data}
              selectedLayer={selectedLayer}
              onLayerSelected={handleSelectedLayer}
              onUpdateLayer={layers.update}
              onDeleteLayer={layers.del}
              images={images.data}
              /*
                Le point d'entrée dépend de l'application : le canevas partagé
                ne connaît pas la modale de sélection d'acte, propre au
                gestionnaire. Il n'affiche donc que ce qu'on lui donne.
              */
              emptyState={
                <>
                  <Header icon>
                    <Icon
                      name="file image"
                      style={{ alignContent: 'center' }}
                    />
                    Aucun acte sélectionné
                  </Header>
                  <Button
                    icon="search"
                    onClick={() => setOpenSelectionModal(true)}
                    content="Sélectionnez l'acte que vous voulez modifier"
                    color="blue"
                    style={{ maxWidth: 'fit-content' }}
                    disabled={blocked}
                  />
                </>
              }
            />
          </div>
          <div
            style={{
              display: 'flex',
              flex: 3,
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <SectionPages
              isTiff={isTiff}
              onPageChange={setSelectedPage}
              selectedPage={selectedPage}
              pages={pages}
            />
          </div>
        </div>
      </div>
    </CanvasContextProvider>
  );
};

export default Gestionnaire;
